import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from "primereact/utils";
import { useTranslation } from "react-i18next";
import PlanGroupActions from "../../Redux/transition/plan-group/plan-group.reducer";

import CustomToast from '../../Components/CustomToast';
import PlanActions from "../../Redux/transition/plan/plan.reducer";
import "../../Styles/pages/product.scss";

export default function PlanAddRemoveWatcher({ onHide, visible, selectedPlan }) {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();

    const [watcherInput, setWatcherInput] = useState("");
    const [selectedWatchers, setSelectedWatchers] = useState([]);
    const [error, setError] = useState("");
    const [clickAdd, setClickAdd] = useState(false);
    const [clickRemove, setClickRemove] = useState(false);

    const [toastConfig, setToastConfig] = useState({
        severity: "",
        detail: "",
        summary: "",
        life: 0
    });
    const [toastTrigger, setToastTrigger] = useState(false);
    const [toastType, setToastType] = useState("");

    const { addingWatchersToPlan, addedWatchersToPlan, addingWatchersToPlanError, removingWatcherFromPlan, removedWatcherFromPlan,
        watchers, fetchingWatchers } = useSelector((state) => state.plans);
    const business = useSelector((state) => state.businesses.business);

    const businessId = business.id

    const addWatchersToPlan = useCallback((watcherContent) => dispatch(PlanActions.planAddWatchersRequest(watcherContent)), [dispatch]);
    const removeWatcherFromPlan = useCallback((watcherId) => dispatch(PlanActions.planRemoveWatcherRequest(watcherId)), [dispatch]);
    const getAllWatchers = useCallback((planId) => dispatch(PlanActions.planAllWatchersRequest(planId)), [dispatch]);
    const getActivePlanGroups = useCallback((businessId) => { dispatch(PlanGroupActions.planGroupGraphedActiveRequest(businessId)); }, [dispatch]);

    useEffect(() => {
        if (visible && selectedPlan?.id) {
            getAllWatchers(selectedPlan.id);
            setWatcherInput("");
            setSelectedWatchers([]);
            setError("");
        }
    }, [visible, selectedPlan, getAllWatchers]);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const handleChange = (e) => {
        setWatcherInput(e.target.value);
        if (error) setError("");
    };

    const handleAddWatcher = () => {
        if (!selectedPlan?.id) return;

        if (!watcherInput) {
            setError(t("colleague.email_error"));
            return;
        }

        if (!emailRegex.test(watcherInput)) {
            setError(t("common.invalid_email"));
            return;
        }

        if (watchers?.some(w => w.email === watcherInput)) {
            setError(t("common.existing_email"));
            return;
        }

        addWatchersToPlan({
            email: watcherInput,
            planId: selectedPlan.id
        });

        setClickAdd(true);
        setError("");
    };

    const handleRemoveWatchers = () => {
        if (!selectedPlan?.id || selectedWatchers.length === 0) return;

        const selectedWatcherId = selectedWatchers[0]?.id;

        setClickRemove(true);
        removeWatcherFromPlan(selectedWatcherId);
        setSelectedWatchers([]);
    };

    const onWatcherSelect = (watcher) => {
        setSelectedWatchers(prev =>
            prev[0]?.id === watcher.id ? [] : [watcher]
        );
    };

    const addingWatcherErrorMessage = addingWatchersToPlanError?.detail?.split('"')?.[1] ?? addingWatchersToPlanError?.detail;

    const showToast = (type, severity, summary, detail) => {
        setToastConfig({
            severity,
            summary,
            detail,
            life: 4000,
        });
        setToastType(type);
        setToastTrigger(true);
    };

    useEffect(() => {
        if (!addingWatchersToPlan && clickAdd && addedWatchersToPlan) {
            showToast("add", "success", t("success"), t("plan.watcher_success_summary"));
        }
        if (!addingWatchersToPlan && clickAdd && addingWatchersToPlanError) {
            showToast("add", "error", t("error.error"), addingWatcherErrorMessage);
        }
        if (!removingWatcherFromPlan && clickRemove) {
            showToast("remove", "success", t("success"), t("plan.watcher_remove_detail_success"));
        }
        if (!removingWatcherFromPlan && clickRemove && removedWatcherFromPlan) {
            showToast("remove", "error", t("error.error"), t("plan.watcher_remove_detail_error"));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addingWatchersToPlan, clickAdd, addedWatchersToPlan, addingWatchersToPlanError,
        removingWatcherFromPlan, removedWatcherFromPlan, clickRemove, addingWatcherErrorMessage
    ]);

    const handleToastClose = () => {
        setToastTrigger(false);

        if (toastType === "add") {
            setWatcherInput("");
            setClickAdd(false);
        }

        if (toastType === "remove") {
            setClickRemove(false);
        }
        getAllWatchers(selectedPlan?.id);
    };

    const handleErrorToastClose = () => {
        setToastTrigger(false);
        setSelectedWatchers([]);

        if (toastType === "add") {
            setClickAdd(false);
        }

        if (toastType === "remove") {
            setClickRemove(false);
        }
    };

    return (
        <Dialog
            header={t("common.add_remove_watcher")}
            visible={visible}
            style={{ width: '350px' }}
            contentStyle={{ overflow: "visible" }}
            onHide={() => {
                getActivePlanGroups(businessId);
                onHide()
            }}
        >
            <CustomToast
                {...toastConfig}
                trigger={toastTrigger}
                handleActionsAfterToast={handleToastClose}
                handleActionsAfterErrorToast={handleErrorToastClose}
            />

            <div className='p-3'>
                <p className='watcher_email'>{t("common.watcher_email")}</p>

                <div className="flex gap-2 items-start">
                    <div className="flex-1">
                        <InputText
                            value={watcherInput}
                            onChange={handleChange}
                            style={{ width: "100%", height: "2.5rem" }}
                            className={classNames({ "p-invalid": error })}
                            placeholder={t("common.enter_email")}
                            disabled={selectedWatchers.length > 0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleAddWatcher();
                            }}
                        />
                        {error && (
                            <span className="p-error error_phone_required">{error}</span>
                        )}
                    </div>

                    <Button
                        label={t("tags.add_btn_text")}
                        className='add_watcher_btn'
                        onClick={handleAddWatcher}
                        loading={addingWatchersToPlan}
                        disabled={!watcherInput.trim()}
                    />
                </div>

                <hr className='hr_style' />

                <div className="watcher-list mb-4">
                    {fetchingWatchers ? (
                        <div className="spinning-div">
                            <ProgressSpinner className="spinning-item" />
                        </div>
                    ) : watchers?.length > 0 ? (
                        watchers.map((watcher) => (
                            <div key={watcher.email} className="field-checkbox">
                                <Checkbox
                                    inputId={watcher.email}
                                    value={watcher}
                                    onChange={() => onWatcherSelect(watcher)}
                                    checked={selectedWatchers[0]?.id === watcher.id}
                                />
                                <label htmlFor={watcher.email}>{watcher.email}</label>
                            </div>
                        ))
                    ) : (<p className="no_plan_watchers">{t("plan.no_plan_watchers")}</p>)}
                </div>

                <Button
                    label={t("tags.remove_btn_text")}
                    className='remove_watcher_btn right-aligned mt-2'
                    disabled={selectedWatchers.length === 0}
                    onClick={handleRemoveWatchers}
                    loading={removingWatcherFromPlan}
                />
            </div>
        </Dialog>
    );
}